import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Sparkles, ChevronDown, ChevronUp, Calendar, Battery, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENERGY_LABELS = ['', 'Drained', 'Low', 'Okay', 'Good', 'Energised'];
const ENERGY_COLORS = ['', '#ef4444', '#f97316', '#eab308', '#22c55e', '#00D9FF'];

const getWeekStart = (offset = 0) => {
  const d = new Date();
  const dow = d.getDay() === 0 ? 7 : d.getDay();   // Sunday counts as the END of the week
  d.setDate(d.getDate() - dow + 1 + offset * 7);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const formatWeekLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const end = new Date(d); end.setDate(end.getDate() + 6);
  return `${d.toLocaleDateString('en', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en', { month: 'short', day: 'numeric' })}`;
};

const EnergyPicker = ({ value, onChange }) => (
  <div className="flex gap-2">
    {[1,2,3,4,5].map(n => (
      <button key={n} onClick={() => onChange(n)}
        className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${value === n ? 'text-gray-900' : 'text-gray-400 hover:text-white'}`}
        style={{
          background: value === n ? ENERGY_COLORS[n] : 'rgba(255,255,255,0.05)',
          border: `1px solid ${value === n ? ENERGY_COLORS[n] : 'rgba(255,255,255,0.1)'}`,
          transform: value === n ? 'scale(1.05)' : 'scale(1)',
        }}>
        {ENERGY_LABELS[n]}
      </button>
    ))}
  </div>
);

const PastReflection = ({ reflection }) => {
  const [open, setOpen] = useState(false);
  const color = ENERGY_COLORS[reflection.energy_level] || '#00D9FF';
  return (
    <div className="glass-card overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/2 transition-colors text-left">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
            <Calendar size={14} style={{ color }}/>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{formatWeekLabel(reflection.week_start)}</p>
            <p className="text-xs mt-0.5" style={{ color }}>Energy: {ENERGY_LABELS[reflection.energy_level]}</p>
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-gray-500"/> : <ChevronDown size={16} className="text-gray-500"/>}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/5">
          <div className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1.5">What went well</p>
            <p className="text-sm text-gray-300">{reflection.went_well}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1.5">Challenges</p>
            <p className="text-sm text-gray-300">{reflection.challenges}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1.5">Focus for next week</p>
            <p className="text-sm text-gray-300">{reflection.focus_next_week}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const WeeklyReflection = () => {
  const { auth } = useContext(AuthContext);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [form, setForm] = useState({ went_well: '', challenges: '', energy_level: 3, focus_next_week: '' });
  const [saved, setSaved] = useState(false);

  const weekStart = getWeekStart(weekOffset);

  const loadReflections = async () => {
    try {
      const res = await axios.get(`${API}/reflections`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setPast(res.data || []);
      // Prefill form if reflection for this week exists
      const existing = res.data.find(r => r.week_start === weekStart);
      if (existing) {
        setForm({ went_well: existing.went_well, challenges: existing.challenges, energy_level: existing.energy_level, focus_next_week: existing.focus_next_week });
        setSaved(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReflections(); }, [weekStart]);

  const handleSave = async () => {
    if (!form.went_well.trim() || !form.challenges.trim() || !form.focus_next_week.trim()) {
      toast.error('Please fill in all fields'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/reflections`, { week_start: weekStart, ...form },
        { headers: { Authorization: `Bearer ${auth.token}` } });
      toast.success('Reflection saved ✨');
      setSaved(true);
      loadReflections();
    } catch (e) {
      toast.error('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const pastFiltered = past.filter(r => r.week_start !== weekStart);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <Loader2 size={28} className="text-cyan-400 animate-spin"/>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 px-4 pb-16" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#00D9FF,#0EA5E9)' }}>
              <Sparkles size={16} color="#0A0E27"/>
            </div>
            <h1 className="heading-font text-2xl font-bold text-white">Weekly Reflection</h1>
          </div>
          <p className="text-gray-400 text-sm">A moment to pause, look back, and set your intention for the week ahead.</p>
        </div>

        {/* Week selector */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => { setWeekOffset(o => o - 1); setSaved(false); setForm({ went_well:'', challenges:'', energy_level:3, focus_next_week:'' }); }}
            className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>← Prev</button>
          <div className="flex-1 text-center">
            <span className="text-sm font-semibold text-white">{formatWeekLabel(weekStart)}</span>
            {weekOffset === 0 && <span className="ml-2 text-xs text-cyan-400 font-medium">This week</span>}
          </div>
          {weekOffset < 0 && (
            <button onClick={() => { setWeekOffset(o => o + 1); setSaved(false); setForm({ went_well:'', challenges:'', energy_level:3, focus_next_week:'' }); }}
              className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white transition-colors"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>Next →</button>
          )}
          {weekOffset === 0 && <div className="w-16"/>}
        </div>

        {/* Form */}
        <div className="glass-card p-6 mb-8 space-y-6">
          {saved && (
            <div className="flex items-center gap-2 p-3 rounded-xl text-sm text-green-400"
              style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <CheckCircle size={15}/> Reflection saved for this week. You can update it below.
            </div>
          )}

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">✅ What went well this week?</label>
            <textarea rows={3} value={form.went_well}
              onChange={e => setForm(f => ({ ...f, went_well: e.target.value }))}
              placeholder="Tasks completed, moments of focus, wins big or small…"
              className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none resize-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}/>
          </div>

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">⚡ What was challenging?</label>
            <textarea rows={3} value={form.challenges}
              onChange={e => setForm(f => ({ ...f, challenges: e.target.value }))}
              placeholder="What got in the way? What felt hard or draining?"
              className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none resize-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}/>
          </div>

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-3">
              <Battery size={12} className="inline mr-1"/> How was your energy this week?
            </label>
            <EnergyPicker value={form.energy_level} onChange={v => setForm(f => ({ ...f, energy_level: v }))}/>
          </div>

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider mb-2">🎯 What's your focus for next week?</label>
            <textarea rows={2} value={form.focus_next_week}
              onChange={e => setForm(f => ({ ...f, focus_next_week: e.target.value }))}
              placeholder="One or two intentions — what matters most?"
              className="w-full rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none resize-none"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}/>
          </div>

          <button onClick={handleSave} disabled={saving}
            className="w-full teal-btn py-3 text-sm font-semibold flex items-center justify-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin"/> : <Sparkles size={15}/>}
            {saving ? 'Saving…' : saved ? 'Update Reflection' : 'Save Reflection'}
          </button>
        </div>

        {/* Past reflections */}
        {pastFiltered.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Past Reflections</h2>
            <div className="space-y-3">
              {pastFiltered.map(r => <PastReflection key={r.week_start} reflection={r}/>)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default WeeklyReflection;
